import maxBy from 'lodash/maxBy'
import { FileCategories } from '@/constants/backend/FileCategories'
import Avatar from '@/models-ts/user/Avatar'
import { getUrl } from '@/utils/file'
import { parseJson } from '@/utils/parser'

const MAX_IMAGE_WIDTH = 1600

export default class Image {
  id: number | null
  description: string | null
  name: string | null
  private _backendData: any | null
  private _src: string | null
  private _srcSet: string | null
  private _base64: string | null

  constructor(
    data?: Partial<Image> & {
      backendData?: any
      src?: string | null
      srcSet?: string | null
      base64?: string | null
    }
  ) {
    Object.assign(this, {
      id: data?.id,
      name: data?.name,
      description: data?.description,
      _src: data?.src,
      _srcSet: data?.srcSet,
      _backendData: data?.backendData,
      _base64: data?.base64 || '',
    })
  }

  static fromServer(data: LX.Image[] | LX.Image, { category = FileCategories.CAT_IMG_ORIGINAL, maxSize = MAX_IMAGE_WIDTH } = {}) {
    const imagesData = (Array.isArray(data) ? data : [data]).filter(Boolean)
    if (imagesData?.length) {
      interface ImageData {
        src: string
        width: string
      }
      const images = imagesData
        .map((file) => {
          const width = parseJson(file.meta).width
          if (width && width < maxSize) {
            return { src: getUrl(file), width }
          }
        })
        .filter(Boolean) as ImageData[]
      const maxImage = maxBy(images, 'width')
      const srcSet = images.map((file) => `${file.src} ${file.width}w`).join(', ')
      const byCat = imagesData.find((file) => file.category && file.category === category)
      const original = imagesData.find((file) => !file.category || file.category === FileCategories.CAT_IMG_ORIGINAL)
      const img = byCat || original
      return new Image({
        id: img?.id || null,
        src: maxImage?.src || getUrl(img),
        srcSet: srcSet,
        name: img?.name || null,
        description: img?.description || null,
        backendData: imagesData,
      })
    }
    return new Image()
  }

  static arrayFromServer(images: LX.Image[], { category = FileCategories.CAT_IMG_ORIGINAL, maxSize = MAX_IMAGE_WIDTH } = {}) {
    return (images || [])
      .filter((file) => !file.category || file.category === FileCategories.CAT_IMG_ORIGINAL)
      .map((o) => [o, ...images.filter((file) => file.filename.includes(o.filename))])
      .map((img) => Image.fromServer(img, { category, maxSize }))
  }

  setBase64(file: string | null) {
    this._base64 = file
  }

  clear() {
    this._base64 = null
    this._src = null
    this._srcSet = null
    this._backendData = null
  }

  toServer() {
    return this._base64 ? [{ base64: this._base64 }] : this._backendData
  }

  toAvatar() {
    return new Avatar({
      id: this.id,
      src: this.src,
      srcSet: this.srcSet,
      description: this.description,
      name: this.name,
    })
  }

  get src(): string | null {
    return this._base64 || this._src
  }

  get srcSet(): string | null {
    return this._base64 || this._srcSet
  }

  get uploaded(): boolean {
    return !this._base64
  }
}
